import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { api } from '../services/api';
import type { CreateLoanRequest } from '../types';

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-MX');
}

export function LoansPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CreateLoanRequest>({
    debtor_name: '',
    debtor_phone: '',
    amount: 0,
    due_date: '',
    reason: '',
  });

  const { data: loans, isLoading } = useQuery({
    queryKey: ['loans'],
    queryFn: () => api.getLoans(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateLoanRequest) => api.createLoan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setShowForm(false);
      setFormData({ debtor_name: '', debtor_phone: '', amount: 0, due_date: '', reason: '' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.amount > 0 && formData.debtor_name && formData.due_date) {
      createMutation.mutate(formData);
    }
  };

  // Calculate summaries
  const pendingLoans = loans?.filter((l) => l.status !== 'paid') || [];
  const overdueLoans = pendingLoans.filter((l) => new Date(l.due_date) < new Date());
  const totalPending = pendingLoans.reduce((sum, l) => sum + (l.amount - l.paid_amount), 0);

  return (
    <div>
      <Header title="Préstamos" />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="text-2xl mb-2">💵</div>
          <div className="stat-value">{formatMoney(totalPending)}</div>
          <div className="stat-label">Total por cobrar</div>
        </div>
        <div className="stat-card">
          <div className="text-2xl mb-2">📝</div>
          <div className="stat-value">{pendingLoans.length}</div>
          <div className="stat-label">Préstamos activos</div>
        </div>
        <div className="stat-card">
          <div className="text-2xl mb-2">⚠️</div>
          <div className="text-3xl font-bold text-red-400">{overdueLoans.length}</div>
          <div className="stat-label">Vencidos</div>
        </div>
      </div>

      {/* Add Button / Form */}
      <div className="card mb-6">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
          >
            ➕ Nuevo Préstamo
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-lg font-semibold text-primary-500">Nuevo Préstamo</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Nombre del deudor</label>
                <input
                  type="text"
                  className="input"
                  value={formData.debtor_name}
                  onChange={(e) => setFormData({ ...formData, debtor_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">Teléfono</label>
                <input
                  type="tel"
                  className="input"
                  value={formData.debtor_phone}
                  onChange={(e) => setFormData({ ...formData, debtor_phone: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Monto</label>
                <input
                  type="number"
                  className="input"
                  step="100"
                  value={formData.amount || ''}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div>
                <label className="label">Fecha de vencimiento</label>
                <input
                  type="date"
                  className="input"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <label className="label">Razón</label>
              <input
                type="text"
                className="input"
                placeholder="Motivo del préstamo"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              />
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Guardando...' : 'Registrar Préstamo'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Loans List */}
      <div className="card">
        <h2 className="text-lg font-semibold text-primary-500 mb-4">📋 Cuentas por Cobrar</h2>
        {isLoading ? (
          <p className="text-gray-500 text-center py-4">Cargando...</p>
        ) : (
          <div className="space-y-3">
            {loans?.map((loan) => {
              const isOverdue = new Date(loan.due_date) < new Date() && loan.status !== 'paid';
              const remaining = loan.amount - loan.paid_amount;

              return (
                <div key={loan.id} className="p-4 bg-dark-bg rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold text-lg">{loan.debtor_name}</span>
                    <span className={`badge ${isOverdue ? 'badge-danger' : loan.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                      {isOverdue ? 'VENCIDO' : loan.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm text-gray-500">{loan.reason || 'Sin motivo'}</div>
                      <div className="text-sm text-gray-500">Vence: {formatDate(loan.due_date)}</div>
                    </div>
                    <div className="text-2xl font-bold text-primary-500">
                      {formatMoney(remaining)}
                    </div>
                  </div>
                </div>
              );
            })}
            {!loans?.length && (
              <p className="text-gray-500 text-center py-4">No hay préstamos activos</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
